import React, { useContext } from 'react';
import { Document, Page } from 'react-pdf';
import { LanguageContext } from '../../contexts/Language';
import { t } from '../../contexts/Language/reducer';
import { usePdfLoader } from '../../hooks/usePdfLoader';
import { PortfolioPage, isSinglePage, isUnfoldPage } from '../../types/portfolio';

interface PdfMobileViewProps {
  pdfUrls: string[] | PortfolioPage[];
}

/**
 * Mobile-optimized scrollable PDF viewer
 * Displays PDFs as stacked pages for easy vertical scrolling
 */
export const PdfMobileView: React.FC<PdfMobileViewProps> = ({ pdfUrls }) => {
  const [languageState] = useContext(LanguageContext);

  // Normalize input to PortfolioPage format
  const pages: PortfolioPage[] = React.useMemo(() => {
    if (pdfUrls.length === 0) return [];

    // Check if already in new format
    if (typeof pdfUrls[0] === 'object' && 'type' in pdfUrls[0]) {
      return pdfUrls as PortfolioPage[];
    }

    // Convert old format (string[]) to new format
    return (pdfUrls as string[]).map(url => ({ type: 'single', url }));
  }, [pdfUrls]);

  // Extract all PDF URLs for loader
  const allPdfUrls = React.useMemo(() => {
    return pages.flatMap(page =>
      isSinglePage(page) ? [page.url] : [page.preview, ...page.pdfs]
    );
  }, [pages]);

  const { loading, error } = usePdfLoader(allPdfUrls);
  const containerWidth = typeof window !== 'undefined' ? window.innerWidth * 0.95 : 300;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <p className="text-xl text-gray-600">{t('pdf-loading', languageState)}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-6">
        <p className="text-xl text-red-600 mb-2">{t('pdf-error', languageState)}</p>
        <p className="text-sm text-gray-500">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="w-full overflow-y-auto p-4">
      {pages.map((page, index) => (
        <div key={index} className="mb-6">
          <p className="text-center mb-2 text-sm text-gray-600">
            {t('page', languageState)} {index + 1} {t('of', languageState)} {pages.length}
          </p>

          {isUnfoldPage(page) ? (
            // Render unfold page based on layout
            <div className="space-y-2">
              {page.layout.startsWith('1x') ? (
                // Horizontal scrollable strip for 1x layouts
                <div className="overflow-x-auto gatefold-scroll-container">
                  <div className="flex pb-2">
                    {page.pdfs.map((pdfUrl, pdfIndex) => (
                      <div key={pdfIndex} className="gatefold-page flex-shrink-0">
                        <Document file={pdfUrl}>
                          <Page
                            pageNumber={1}
                            width={(containerWidth * 0.7)} // 70% width for horizontal scroll
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            loading={
                              <div
                                className="flex items-center justify-center bg-gray-100"
                                style={{
                                  width: containerWidth * 0.7,
                                  height: (containerWidth * 0.7) * 1.414,
                                }}
                              >
                                <span className="text-gray-500 text-xs">Loading...</span>
                              </div>
                            }
                            error={
                              <div
                                className="flex items-center justify-center bg-gray-100"
                                style={{
                                  width: containerWidth * 0.7,
                                  height: (containerWidth * 0.7) * 1.414,
                                }}
                              >
                                <span className="text-red-500 text-xs">Error</span>
                              </div>
                            }
                          />
                        </Document>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                // 2x2 grid for 2x2 layout
                <div className="grid grid-cols-2 gap-2">
                  {page.pdfs.map((pdfUrl, pdfIndex) => (
                    <Document key={pdfIndex} file={pdfUrl}>
                      <Page
                        pageNumber={1}
                        width={(containerWidth - 8) / 2} // 2 columns with gap
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        loading={
                          <div
                            className="flex items-center justify-center bg-gray-100"
                            style={{
                              width: (containerWidth - 8) / 2,
                              height: ((containerWidth - 8) / 2) * 1.414,
                            }}
                          >
                            <span className="text-gray-500 text-xs">Loading...</span>
                          </div>
                        }
                        error={
                          <div
                            className="flex items-center justify-center bg-gray-100"
                            style={{
                              width: (containerWidth - 8) / 2,
                              height: ((containerWidth - 8) / 2) * 1.414,
                            }}
                          >
                            <span className="text-red-500 text-xs">Error</span>
                          </div>
                        }
                      />
                    </Document>
                  ))}
                </div>
              )}
              <p className="text-center text-xs text-gray-500">
                {page.layout} gatefold ({page.pdfs.length} pages)
              </p>
            </div>
          ) : (
            // Render single page
            <Document file={page.url}>
              <Page
                pageNumber={1}
                width={containerWidth}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                loading={
                  <div
                    className="flex items-center justify-center bg-gray-100"
                    style={{ width: containerWidth, height: containerWidth * 1.414 }}
                  >
                    <span className="text-gray-500">Loading...</span>
                  </div>
                }
                error={
                  <div
                    className="flex items-center justify-center bg-gray-100"
                    style={{ width: containerWidth, height: containerWidth * 1.414 }}
                  >
                    <span className="text-red-500">Error</span>
                  </div>
                }
              />
            </Document>
          )}
        </div>
      ))}
    </div>
  );
};
